// Sprint-V25 Meta-Pixel-Consolidation (Sa 12.09.2026, Boardroom-CoS-Auftrag)
//
// Zentrale Utility fuer alle Meta-Pixel Standard-Events. Der Base-Code
// (Pixel-ID 308766585237580, fbq('init'), fbq('track','PageView')) ist
// bereits in /public/index.html <head> gesetzt — Consent-gated, laedt
// aber unabhaengig von der SPA-Route und feuert PageView bei jedem Load.
//
// Zweck dieses Moduls: einheitliches Interface fuer die 3 Conversion-Events
// (Lead, CompleteRegistration, Purchase) mit:
//   - `typeof window.fbq === 'function'`-Guard (Consent-safe),
//   - Idempotenz-Sperre per window.__f4s_pixel_tracked (verhindert Doppel-
//     Feuerungen bei React-18-StrictMode / Doppel-Renders / Retries),
//   - Fire-and-forget (Analytics-Fehler brechen die UX nie).
//
// Verwendung im React-Code:
//   import { trackLead, trackCompleteRegistration, trackPurchase } from "@/lib/metaPixel";
//   trackLead({ source: "landing_optin" });
//   trackCompleteRegistration({ userId });
//   trackPurchase({ value: 39.0, currency: "EUR", plan: "pro_monthly" });

function _once(key) {
  if (typeof window === "undefined") return false;
  window.__f4s_pixel_events = window.__f4s_pixel_events || {};
  if (window.__f4s_pixel_events[key]) return false;
  window.__f4s_pixel_events[key] = true;
  return true;
}

function _fbq(eventName, payload = {}, options) {
  try {
    if (typeof window === "undefined") return;
    if (typeof window.fbq !== "function") return; // Consent noch nicht erteilt
    if (options && Object.keys(options).length > 0) {
      window.fbq("track", eventName, payload, options);
    } else {
      window.fbq("track", eventName, payload);
    }
  } catch (_e) {
    // Fire-and-forget — Analytics-Fehler duerfen die UX nie stoeren.
  }
}

/** Feuert das Meta-Standard-Event `Lead`.
 *  Trigger: NUR nach bestaetigtem DOI (WelcomePage.jsx) — Andreas-Direktive
 *  vom Sa 12.09.2026 spät: Lead darf nicht bei jedem Magic-Request feuern,
 *  sonst optimiert Meta auf angefordert-aber-nie-bestaetigte-Interessenten.
 *  Idempotenz per userId (falls vorhanden), sonst per source.
 */
export function trackLead({
  source = "doi_confirmed",
  userId = null,
  value = 0,
  currency = "EUR",
  eventId = null,
} = {}) {
  const key = userId ? `Lead:${userId}` : `Lead:${source}`;
  if (!_once(key)) return;
  const options = eventId ? { eventID: eventId } : undefined;
  _fbq("Lead", {
    content_name: source,
    content_category: "doi_confirmed",
    value,
    currency,
  }, options);
}

/** Feuert das Meta-Standard-Event `CompleteRegistration`.
 *  Trigger: nach erfolgreichem DOI-Handoff (WelcomePage.jsx) — User hat
 *  seinen Magic-Link geklickt UND ist eingeloggt.
 *  `eventId` MUSS an das Backend-CAPI-Event matchen (Sprint V26 Dedup).
 */
export function trackCompleteRegistration({ userId = null, method = "magic_link_doi", eventId = null } = {}) {
  const key = userId ? `CompleteRegistration:${userId}` : `CompleteRegistration:anon`;
  if (!_once(key)) return;
  const options = eventId ? { eventID: eventId } : undefined;
  _fbq("CompleteRegistration", {
    content_name: method,
    status: "confirmed",
  }, options);
}

/** Meta-Standard-Event `InitiateCheckout` (Klick auf Checkout-CTA). */
export function trackInitiateCheckout({ value, currency = "EUR", contentName, position }) {
  _fbq("InitiateCheckout", { value, currency, content_name: contentName, content_category: position });
}

/** Feuert das Meta-Standard-Event `Purchase`.
 *  Trigger: nach erfolgreichem Ablefy-Checkout-Redirect / Webhook-Success.
 *  `eventId` MUSS an das Backend-CAPI-Event matchen (Sprint V26 Dedup).
 */
export function trackPurchase({ value, currency = "EUR", plan = "unknown", orderId = null, eventId = null } = {}) {
  if (typeof value !== "number" || value <= 0) return;
  const key = orderId ? `Purchase:${orderId}` : `Purchase:${plan}:${Date.now()}`;
  if (!_once(key)) return;
  const options = eventId ? { eventID: eventId } : undefined;
  _fbq("Purchase", {
    value,
    currency,
    content_name: plan,
  }, options);
}
